import React, { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { Conversation } from '@dazzle/api-client';

import useFirebaseRealtimeDatabase from 'Common/hooks/useRealtimeDatabase';

import { useOrdersContext } from 'Compass/contexts/OrdersContext';
import { app } from 'Compass/api/Firebase';
import { ConversationMessage } from 'Compass/components/ConversationMessage';
import { formatRelativeTimestamp } from 'Common/util/Date';

const OrderConversation = ({ userId, conversationId, onImageClick }: {
  userId: string;
  conversationId: string;
  onImageClick: (imageUrl: string) => void;
}) => {
  const lastModifiedTime = useFirebaseRealtimeDatabase<string>(`dazzle/user/${userId}/conversations`, app);
  const ordersContext = useOrdersContext();

  const [conversation, setConversation] = useState<Conversation>();

  const conversationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if ((conversation?.messages?.length ?? 0) > 0) {
      conversationRef.current?.scrollTo({ top: conversationRef.current.scrollHeight });
    }
  }, [conversation]);

  useEffect(() => {
    const getConversation = async () => {
      const conversation = await ordersContext.fetchConversation(conversationId);

      setConversation(conversation);
    };

    if (lastModifiedTime !== undefined) {
      void getConversation();
    }
  }, [lastModifiedTime, conversationId, ordersContext]);

  const isPending = conversation?.messages?.length === 1 && !conversation?.messages?.[0].show_to_user;

  return (
    <ConversationContainer ref={conversationRef} data-testid="conversation-container">
      {conversation?.messages && conversation.messages.length > 0 ? (
        (() => {
          let lastRelativeTimestamp: string | undefined;
          return conversation.messages.map((message) => {
            const relativeTimestamp = formatRelativeTimestamp(message.message_ts);
            const shownTimestamp = lastRelativeTimestamp !== relativeTimestamp ? relativeTimestamp : undefined;
            lastRelativeTimestamp = relativeTimestamp;

            return (
              <StreamingMessageContainer $isPending={isPending} key={message.id}>
                {isPending ? <MessageFlag>Draft</MessageFlag> : null}
                <ConversationMessage message={message} onImageClick={onImageClick} relativeTimestamp={shownTimestamp} />
              </StreamingMessageContainer>
            );
          });
        })()
      ) : (
        <EmptyState>No messages yet</EmptyState>
      )}
    </ConversationContainer>
  );
};


const ConversationContainer = styled.div`
  background: #F8F9FC;
  border-radius: 12px;
  padding: 16px;
  overflow-y: auto;
  max-height: min(100vh, 600px);

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #F0F1F6;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #CDD1DC;
    border-radius: 3px;

    &:hover {
      background: #8890A5;
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
  color: #8890A5;
  font-size: 14px;
  font-style: italic;
`;


const StreamingMessageContainer = styled.div<{ $isPending: boolean }>`
  ${({ $isPending }) => $isPending ? 'border: 1px dashed #FF9966;' : ''}
  border-radius: 5px;
  position: relative;
  overflow: hidden;

  p {
    margin-bottom: 15px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const MessageFlag = styled.div`
  position: absolute;
  text-transform: uppercase;
  rotate: -45deg;
  background: #FF9966;
  padding: 0 35px;
  top: 15px;
  left: -33px;
  width: 120px;
`;
export default OrderConversation;
